
import { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import QuestionCard from '@/components/assessment/QuestionCard';
import ProgressIndicator from '@/components/assessment/ProgressIndicator';
import NavigationControls from '@/components/assessment/NavigationControls';
import SuccessMessage from '@/components/assessment/SuccessMessage';
import LoadingScreen from '@/components/assessment/LoadingScreen';
import WelcomeOverlay from '@/components/assessment/WelcomeOverlay';
import { useAssessmentState } from '@/hooks/useAssessmentState';
import { useSessionActivity } from '@/hooks/useSessionActivity';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const Assessment = () => {
  const navigate = useNavigate();
  const {
    questions,
    currentQuestionIndex,
    responses,
    isSubmitting,
    isGeneratingReport,
    showSuccessMessage,
    submitError,
    isQuestionVisible,
    direction,
    isInitializing,
    initError,
    canEarlySubmit,
    showWelcomeOverlay,
    handleWelcomeClose,
    handleOptionSelect,
    handleNext,
    handlePrevious,
    handleSubmit,
  } = useAssessmentState();
  
  // Disable session timeout when submitting or generating reports
  useSessionActivity(60, undefined, isSubmitting || isGeneratingReport);

  // Log assessment status for debugging
  useEffect(() => {
    console.log('Assessment page rendered', {
      isInitializing,
      questionsCount: questions.length,
      initError,
      showSuccessMessage
    });
  }, [isInitializing, questions.length, initError, showSuccessMessage]);

  // Show initialization error screen
  if (initError) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-grow flex flex-col items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-4">Error Loading Assessment</h2>
            <p className="text-gray-700 mb-6">{initError}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.reload()} 
                variant="default" 
                className="w-full"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline" 
                className="w-full"
              >
                Return to Home
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // If questions are still loading or initializing
  if (isInitializing || questions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <LoadingScreen message="Initializing assessment..." />
        <Footer />
      </div>
    );
  }
  
  // Show success message after submission
  if (showSuccessMessage) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <SuccessMessage />
        <Footer />
      </div>
    );
  }

  // Make sure we have a valid current question
  const currentQuestion = questions[currentQuestionIndex];
  if (!currentQuestion) {
    toast.error("Error loading question data");
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <LoadingScreen message="Error loading questions. Please refresh..." />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      {/* Welcome Overlay */}
      <WelcomeOverlay 
        isOpen={showWelcomeOverlay} 
        onClose={handleWelcomeClose} 
      />
      
      <main className="flex-grow flex flex-col items-center justify-center p-4 sm:p-6 md:p-8">
        {/* Progress Indicator */}
        <ProgressIndicator 
          currentQuestionIndex={currentQuestionIndex} 
          totalQuestions={questions.length} 
        />
        
        {/* Question Card */}
        <div className="w-full max-w-3xl">
          <QuestionCard
            question={currentQuestion}
            selectedAnswer={responses[currentQuestion.id]}
            onSelectOption={handleOptionSelect}
            isVisible={isQuestionVisible}
            direction={direction}
          />
          
          {/* Navigation Controls */}
          <NavigationControls
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            currentQuestionAnswered={!!responses[currentQuestion.id]}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isGeneratingReport={isGeneratingReport}
            submitError={submitError}
            canEarlySubmit={canEarlySubmit}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Assessment;
